// Everyone on the list: search, filter, add one person, open a person to edit or turn emails off.

import { useEffect, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { navigate, useLocation } from '../lib/router';
import { Button, Empty, Field, LoadError, Modal, Spinner, confirmBox, toast, toastError, useLoad } from '../lib/ui';
import { Icon } from '../lib/icons';
import { ago, fullName, initials, num, plural, when } from '../lib/format';
import type { Contact, ContactStatus } from '../../shared/types';

interface ListData {
  contacts: Contact[];
  total: number;
  page: number;
  pageSize: number;
  byStatus: Record<string, number>;
  byStage: Record<Stage, number>;
}

type Stage = 'new' | 'interested' | 'ready' | 'regular' | 'quiet';

// The path a client takes, left to right, then the people who have gone quiet.
const STAGES: { v: Stage; label: string; hint: string }[] = [
  { v: 'new', label: 'New', hint: 'New clients from the last 30 days.' },
  { v: 'interested', label: 'Interested', hint: 'Tapped a link in an email lately.' },
  { v: 'ready', label: 'Ready to book', hint: 'Tapped Book and has not come in since. Call them first.' },
  { v: 'regular', label: 'Regulars', hint: 'Came in during the last 4 months.' },
  { v: 'quiet', label: 'Quiet', hint: 'Nothing lately. A good list for a check-in email.' },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((x) => [x.v, x.label])) as Record<Stage, string>;

interface Tag {
  tag: string;
  active: number;
  total: number;
}

const STATUS_LABEL: Record<ContactStatus, string> = {
  active: 'Gets emails',
  unsubscribed: 'Unsubscribed',
  bounced: 'Bad address',
  complained: 'Marked as spam',
};

export function Contacts() {
  const { query } = useLocation();
  const [q, setQ] = useState(query.get('q') || '');
  const [typed, setTyped] = useState(q);
  const [status, setStatus] = useState(query.get('status') || '');
  const [tag, setTag] = useState(query.get('tag') || '');
  const [stage, setStage] = useState(query.get('stage') || '');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  // Wait for a pause in typing before searching.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(typed.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [typed]);

  const params = new URLSearchParams({ q, status, tag, stage, page: String(page) });
  const list = useLoad(() => api<ListData>(`/api/contacts?${params}`), [q, status, tag, stage, page]);
  const tags = useLoad(() => api<{ tags: Tag[] }>('/api/tags').then((r) => r.tags), []);
  const d = list.data;
  const everyone = d ? Object.values(d.byStatus).reduce((a, b) => a + b, 0) : 0;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
  const filtered = !!(q || status || tag || stage);
  // A column that is empty for every row on the page only adds dashes. Hide it.
  const showTags = !!d?.contacts.some((c) => c.tags.length > 0);
  const showVisit = !!d?.contacts.some((c) => !!c.last_visit_at);

  const chips: { v: string; label: string; n: number }[] = d
    ? [
        { v: '', label: 'Everyone', n: everyone },
        { v: 'active', label: 'Gets emails', n: d.byStatus.active || 0 },
        { v: 'unsubscribed', label: 'Unsubscribed', n: d.byStatus.unsubscribed || 0 },
        { v: 'bounced', label: 'Bad address', n: d.byStatus.bounced || 0 },
        { v: 'complained', label: 'Spam', n: d.byStatus.complained || 0 },
      ].filter((c) => c.v === '' || c.v === 'active' || c.n > 0 || c.v === status)
    : [];

  return (
    <div class="wrap">
      <div class="page-head">
        <div>
          <h1 class="display">Contacts</h1>
        </div>
        <div class="head-actions">
          <Button icon="plus" onClick={() => setAdding(true)}>
            Add a person
          </Button>
          <Button kind="primary" icon="upload" onClick={() => navigate('/contacts/import')}>
            Import
          </Button>
          <a class="btn btn-quiet btn-m contacts-download" href="/api/contacts/export.csv" download title="Save a copy of your client list">
            <Icon name="download" size={18} /> Download list
          </a>
        </div>
      </div>

      {d && d.byStatus.active > 0 && (
        <>
          <div class="stages" role="group" aria-label="Where people are">
            {STAGES.map((x) => (
              <button
                key={x.v}
                class={`stage st-${x.v} ${stage === x.v ? 'on' : ''}`}
                aria-pressed={stage === x.v}
                title={x.hint}
                onClick={() => {
                  setStage(stage === x.v ? '' : x.v);
                  setPage(1);
                }}
              >
                <b>{num(d.byStage[x.v] || 0)}</b>
                <span>{x.label}</span>
              </button>
            ))}
          </div>
          {stage && <p class="stage-hint">{STAGES.find((x) => x.v === stage)?.hint}</p>}
        </>
      )}

      <div class="filters">
        <label class="search">
          <Icon name="search" size={18} />
          <input type="search" placeholder="Search" value={typed} onInput={(e) => setTyped(e.currentTarget.value)} aria-label="Search contacts" />
        </label>
        {tags.data && tags.data.length > 0 && (
          <select
            class="select"
            value={tag}
            aria-label="Tag"
            onChange={(e) => {
              setTag(e.currentTarget.value);
              setPage(1);
            }}
          >
            <option value="">All tags</option>
            {tags.data.map((t) => (
              <option key={t.tag} value={t.tag}>
                {t.tag} ({num(t.total)})
              </option>
            ))}
          </select>
        )}
      </div>
      <div class="chips-row contacts-chips">
        {chips.map((c) => (
          <button
            key={c.v}
            class={`filter-chip ${status === c.v ? 'on' : ''}`}
            onClick={() => {
              setStatus(c.v);
              setPage(1);
            }}
          >
            {c.label} <i>{num(c.n)}</i>
          </button>
        ))}
      </div>

      {list.error ? (
        <LoadError error={list.error} retry={list.reload} />
      ) : !d ? (
        <Spinner />
      ) : d.contacts.length === 0 ? (
        filtered ? (
          <Empty icon="search" title="Nobody matches">
            Try a shorter search, or clear the filters.
          </Empty>
        ) : (
          <Empty
            icon="users"
            title="No contacts yet"
            action={
              <Button kind="primary" icon="upload" onClick={() => navigate('/contacts/import')}>
                Import your list
              </Button>
            }
          >
            Bring in your client list from Aesthetic Record or any spreadsheet. It takes about a minute.
          </Empty>
        )
      ) : (
        <>
          <div class={`table people-table ${showTags ? '' : 'no-tags'} ${showVisit ? '' : 'no-visit'}`} role="table" aria-label="Contacts">
            <div class="tr th" role="row">
              <span role="columnheader">Name</span>
              {showTags && (
                <span role="columnheader" class="hide-phone">
                  Tags
                </span>
              )}
              {showVisit && (
                <span role="columnheader" class="hide-phone">
                  Last visit
                </span>
              )}
              <span role="columnheader">Stage</span>
            </div>
            {d.contacts.map((c) => (
              <button class={`tr ${c.status === 'active' && c.stage && c.stage !== 'off' ? `st-${c.stage}` : 'st-off'}`} role="row" key={c.id} onClick={() => setOpenId(c.id)}>
                <span class="person-cell" role="cell">
                  <span class="avatar">{initials(c)}</span>
                  <span class="person-text">
                    <b>{fullName(c)}</b>
                    <small>{c.email}</small>
                  </span>
                </span>
                {showTags && (
                  <span role="cell" class="hide-phone tag-cell">
                    {c.tags.slice(0, 3).map((t) => (
                      <span class="tag" key={t}>
                        {t}
                      </span>
                    ))}
                    {c.tags.length > 3 && <span class="muted">+{c.tags.length - 3}</span>}
                  </span>
                )}
                {showVisit && (
                  <span role="cell" class="hide-phone muted">
                    {c.last_visit_at ? when(c.last_visit_at).replace(/,? \d+:\d+.*$/, '') : '—'}
                  </span>
                )}
                <span role="cell">
                  {c.status === 'active' && c.stage && c.stage !== 'off' ? (
                    <span class={`where st-${c.stage}`} title={`Warmth ${c.score ?? 0} of 100`}>
                      <i class="meter" aria-hidden="true">
                        {[1, 2, 3, 4, 5].map((k) => (
                          <i key={k} class={(c.score ?? 0) >= k * 20 - 10 ? 'on' : ''} />
                        ))}
                      </i>
                      {STAGE_LABEL[c.stage as Stage]}
                    </span>
                  ) : (
                    <span class={`dot-status st-${c.status}`}>{STATUS_LABEL[c.status]}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
          {pages > 1 && (
            <div class="pager">
              <Button size="s" kind="quiet" icon="back" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Back
              </Button>
              <span class="muted">
                Page {page} of {pages} · {plural(d.total, 'person', 'people')}
              </span>
              <Button size="s" kind="quiet" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                More
              </Button>
            </div>
          )}
          <a class="text-link contacts-download-foot" href="/api/contacts/export.csv" download>
            <Icon name="download" size={16} /> Download your list
          </a>
        </>
      )}

      {adding && (
        <AddPerson
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            list.reload();
            tags.reload();
          }}
        />
      )}
      {openId != null && (
        <PersonDrawer
          id={openId}
          allTags={tags.data?.map((t) => t.tag) || []}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            list.reload();
            tags.reload();
          }}
        />
      )}
    </div>
  );
}

function AddPerson({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [f, setF] = useState({ email: '', first_name: '', last_name: '', tags: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: Event) => setF({ ...f, [k]: (e.currentTarget as HTMLInputElement).value });
  const save = async (e?: Event) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/contacts', { body: { ...f, tags: splitTags(f.tags), source: 'added by hand' } });
      toast(`Added ${f.first_name || f.email}.`);
      onAdded();
    } catch (err) {
      setError((err as { message?: string }).message || 'Could not add this person.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Add a person"
      onClose={onClose}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" busy={busy} disabled={!f.email.includes('@')} onClick={() => save()}>
            Add
          </Button>
        </>
      }
    >
      <form onSubmit={save} class="form-grid">
        <Field label="Email" error={error}>
          <input type="email" required value={f.email} onInput={set('email')} autoComplete="off" />
        </Field>
        <div class="two">
          <Field label="First name">
            <input value={f.first_name} onInput={set('first_name')} />
          </Field>
          <Field label="Last name">
            <input value={f.last_name} onInput={set('last_name')} />
          </Field>
        </div>
        <Field label="Tags" hint="Separate with commas. For example: botox, vip">
          <input value={f.tags} onInput={set('tags')} />
        </Field>
        <p class="muted small">Only add people who said yes to getting your emails.</p>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function splitTags(s: string): string[] {
  return s
    .split(/[,;]/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
}

interface PersonData {
  contact: Contact;
  history: { id: number; status: string; sent_at: string | null; opens: number; clicks: number; email_id: number; name: string; subject: string }[];
  events: { type: string; at: string; detail: string | null }[];
}

export function PersonDrawer({ id, allTags, onClose, onChanged }: { id: number; allTags: string[]; onClose: () => void; onChanged: () => void }) {
  const r = useLoad(() => api<PersonData>(`/api/contacts/${id}`), [id]);
  const [f, setF] = useState<{ first_name: string; last_name: string; phone: string; tags: string[] } | null>(null);
  const [newTag, setNewTag] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (r.data) {
      const c = r.data.contact;
      setF({ first_name: c.first_name || '', last_name: c.last_name || '', phone: c.phone || '', tags: c.tags });
    }
  }, [r.data]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, []);

  const c = r.data?.contact;
  // Same digits the Ready to book row uses for its Call and Text buttons.
  const tel = c?.phone?.replace(/[^\d+]/g, '');
  const dirty = !!(c && f && (f.first_name !== (c.first_name || '') || f.last_name !== (c.last_name || '') || f.phone !== (c.phone || '') || f.tags.join() !== c.tags.join()));

  const save = async () => {
    if (!f) return;
    setBusy('save');
    try {
      // Send tags only when they changed, so a name edit never touches them.
      const { tags, ...rest } = f;
      const res = await api<{ contact: Contact }>(`/api/contacts/${id}`, { method: 'PATCH', body: tags.join() !== c!.tags.join() ? f : rest });
      r.set({ ...r.data!, contact: res.contact });
      toast('Saved.');
      onChanged();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(null);
    }
  };

  const turnOff = async () => {
    const ok = await confirmBox({ title: 'Stop emailing this person?', body: `${c!.email} won't get any more emails. You can turn it back on later if they ask.`, ok: 'Stop emails', danger: true });
    if (!ok) return;
    setBusy('off');
    try {
      await api(`/api/contacts/${id}/unsubscribe`, { method: 'POST' });
      toast('Done. They won’t get emails anymore.');
      await r.reload();
      onChanged();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(null);
    }
  };

  const turnOn = async () => {
    const ok = await confirmBox({
      title: 'Turn emails back on?',
      body: 'Only do this if this person asked you to add them back. Emailing someone who unsubscribed can get your email account flagged.',
      ok: 'They asked, turn it on',
    });
    if (!ok) return;
    setBusy('on');
    try {
      await api(`/api/contacts/${id}`, { method: 'PATCH', body: { status: 'active', confirm: true } });
      toast('They’ll get your emails again.');
      await r.reload();
      onChanged();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(null);
    }
  };

  const addTag = (t: string) => {
    const v = t.trim().toLowerCase();
    if (!v || !f || f.tags.includes(v)) return;
    setF({ ...f, tags: [...f.tags, v] });
    setNewTag('');
  };

  return (
    <div class="overlay drawer-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside class="drawer" role="dialog" aria-modal="true" aria-label="Contact">
        <div class="modal-head">
          <h2>{c ? fullName(c) : 'Contact'}</h2>
          <button class="icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </div>
        {r.error ? (
          <div class="modal-body">
            <LoadError error={r.error} retry={r.reload} />
          </div>
        ) : !c || !f ? (
          <Spinner />
        ) : (
          <div class="modal-body drawer-body">
            <div class="drawer-who">
              <span class="avatar avatar-l">{initials(c)}</span>
              <div>
                <b>{c.email}</b>
                <span class={`dot-status st-${c.status}`}>{STATUS_LABEL[c.status]}</span>
              </div>
              {tel && (
                <span class="lead-actions drawer-reach">
                  <a class="icon-btn" href={`tel:${tel}`} aria-label={`Call ${fullName(c)}`} title="Call">
                    <Icon name="call" size={18} />
                  </a>
                  <a class="icon-btn" href={`sms:${tel}`} aria-label={`Text ${fullName(c)}`} title="Text">
                    <Icon name="chat" size={18} />
                  </a>
                </span>
              )}
            </div>

            {c.status !== 'active' && (
              <div class="drawer-note">
                {c.status === 'complained'
                  ? 'This person marked one of your emails as spam, so the app will never email them again.'
                  : c.status === 'bounced'
                    ? 'Email to this address bounced, so the app stopped sending to it. If they give you a new address, add it as a new person.'
                    : 'This person unsubscribed. The app skips them on every send.'}
              </div>
            )}

            <div class="two">
              <Field label="First name">
                <input value={f.first_name} onInput={(e) => setF({ ...f, first_name: e.currentTarget.value })} />
              </Field>
              <Field label="Last name">
                <input value={f.last_name} onInput={(e) => setF({ ...f, last_name: e.currentTarget.value })} />
              </Field>
            </div>
            <Field label="Phone">
              <input type="tel" value={f.phone} onInput={(e) => setF({ ...f, phone: e.currentTarget.value })} />
            </Field>
            <div class="field">
              <span class="field-label">Tags</span>
              <div class="tag-edit">
                {f.tags.map((t) => (
                  <span class="tag tag-x" key={t}>
                    {t}
                    <button aria-label={`Remove ${t}`} onClick={() => setF({ ...f, tags: f.tags.filter((x) => x !== t) })}>
                      <Icon name="x" size={12} />
                    </button>
                  </span>
                ))}
                <input
                  list="all-tags"
                  value={newTag}
                  placeholder="Add a tag"
                  onInput={(e) => setNewTag(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addTag(newTag);
                    }
                  }}
                  onBlur={() => addTag(newTag)}
                />
                <datalist id="all-tags">
                  {allTags.filter((t) => !f.tags.includes(t)).map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
            </div>
            {dirty && (
              <div class="drawer-save">
                <Button kind="primary" busy={busy === 'save'} onClick={save}>
                  Save changes
                </Button>
              </div>
            )}

            <h3 class="drawer-h">Emails</h3>
            {r.data!.history.length === 0 ? (
              <p class="muted small">Hasn't been sent anything yet.</p>
            ) : (
              <ul class="history">
                {r.data!.history.map((h) => (
                  <li key={h.id}>
                    <span class="history-name">{h.name}</span>
                    <span class="muted small">
                      {h.status === 'sent' ? `Sent ${ago(h.sent_at)}` : h.status === 'queued' ? 'Waiting to send' : h.status === 'skipped' ? 'Skipped' : "Couldn't send"}
                      {h.clicks ? ' · clicked' : h.opens ? ' · opened' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div class="drawer-foot">
              <span class="muted small">
                Added {ago(c.created_at)}
                {c.source ? ` · ${c.source}` : ''}
              </span>
              {c.status === 'active' ? (
                <Button kind="quiet" class="danger-text" icon="stop" busy={busy === 'off'} onClick={turnOff}>
                  Stop emailing
                </Button>
              ) : c.status === 'unsubscribed' ? (
                <Button kind="quiet" busy={busy === 'on'} onClick={turnOn}>
                  Turn emails back on
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
